import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import {
  MentionableUser,
  NotificationService,
} from 'src/app/helpers/services/notification.service';

@Component({
  selector: 'app-assign-task-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './assign-task-dialog.component.html',
  styleUrls: ['./assign-task-dialog.component.scss'],
})
export class AssignTaskDialogComponent implements OnInit {
  searchText = '';
  allUsers: MentionableUser[] = [];
  filteredUsers: MentionableUser[] = [];
  selectedUsers: MentionableUser[] = [];
  taskDescription = '';
  loading = true;
  submitting = false;
  submitted = false;

  constructor(
    private dialogRef: MatDialogRef<AssignTaskDialogComponent>,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.notificationService.getMentionableUsers().subscribe({
      next: (res) => {
        this.allUsers = res?.data ?? [];
        this.filteredUsers = [...this.allUsers];
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
  }

  onSearch(): void {
    const q = this.searchText.toLowerCase().trim();
    this.filteredUsers = q
      ? this.allUsers.filter(
          (u) =>
            `${u.name} ${u.lastname}`.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)
        )
      : [...this.allUsers];
  }

  toggle(user: MentionableUser): void {
    const idx = this.selectedUsers.findIndex((u) => u.id === user.id);
    if (idx === -1) this.selectedUsers.push(user);
    else this.selectedUsers.splice(idx, 1);
  }

  isSelected(user: MentionableUser): boolean {
    return this.selectedUsers.some((u) => u.id === user.id);
  }

  removeSelected(user: MentionableUser): void {
    this.selectedUsers = this.selectedUsers.filter((u) => u.id !== user.id);
  }

  get canSubmit(): boolean {
    return (
      this.selectedUsers.length > 0 &&
      this.taskDescription.trim().length > 0 &&
      !this.submitting
    );
  }

  async submit(): Promise<void> {
    if (!this.canSubmit) return;
    this.submitting = true;
    const senderName = this.getSenderName();

    try {
      await Promise.all(
        this.selectedUsers.map((user) =>
          firstValueFrom(
            this.notificationService.createNotification({
              userId: user.id,
              type: 'task_assignment',
              event: 'task_assigned',
              title: `${senderName} assigned you a task`,
              message: this.taskDescription.trim(),
            })
          )
        )
      );
      this.submitted = true;
      setTimeout(() => this.dialogRef.close({ assigned: true }), 1400);
    } catch (err: any) { console.error('[where: AssignTaskDialogComponent.submit] task assignment failed', err);
      this.submitting = false;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  initials(user: MentionableUser): string {
    return `${user.name?.[0] ?? ''}${user.lastname?.[0] ?? ''}`.toUpperCase();
  }

  private getSenderName(): string {
    try {
      const token = localStorage.getItem('token');
      if (!token) return 'Someone';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.name || 'Someone';
    } catch {
      return 'Someone';
    }
  }
}
